import React from 'react';
import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import { fieldsResource } from '@jira/router-resources__business-fields/src/fieldsResource.tsx';
import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { nextGenRoadmapResource } from '@jira/router-resources__roadmap/src/nextGenRoadmapResource.tsx';
import { softwareTimelineRoute } from '@jira/router-routes__software-timeline-routes/src/softwareTimelineRoute.tsx';
import { RoadmapSkeleton } from '@jira/platform__skeletons/src/ui/roadmap/RoadmapSkeleton.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { softwareProjectLayoutCode } from '@jira/platform__layouts-software-project-layout/src/index.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import {
	GSNSoftwareHarmonisedTimelinePageEntrypoint,
	GSNSoftwareTimelinePageEntrypoint,
	softwareHarmonisedTimelinePageEntrypoint,
	softwareTimelinePageEntrypoint,
} from './ui/spa/roadmap/index.tsx';
import type { Route } from '@atlassian/react-resource-router';

export const softwareTimelineRouteEntry: Route = createEntry(softwareTimelineRoute, () => ({
	group: 'software',
	skeleton: fg('timeline_skeleton_update') ? SoftwareTimelineSkeleton : RoadmapSkeleton,

	layout: composeLayouts(
		softwareProjectLayoutCode,
		embeddedIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }]),
	),

	navigation: {
		menuId: MenuIdProjects,
		horizontal: AsyncHorizontalNavJira,
	},

	resources: [
		...getNavigationResources(
			fg('navx-global-side-nav') ? { isUsingSidebarEntrypoint: true } : undefined,
		),
		...getIssueViewResources(),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
		// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
		// eslint-disable-next-line jira/router/no-project-context-resource
		projectContextResource,
		staticProjectSidebarNavigationResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		resourceWithCondition(
			() => !expVal('jira_timeline_harmonisation_m3', 'isEnabled', false),
			nextGenRoadmapResource,
		),
		projectThemeResource,
		resourceWithCondition(() => fg('ai_sprint_goal_gate'), aiEntitlementResource),
		resourceWithCondition(
			() =>
				!fg('jira_software_timeline_perf_improvements') &&
				expVal('jira_timeline_harmonisation_m3', 'isEnabled', false),
			fieldsResource,
		),
	],

	preloadOptions: {
		earlyChunks: !expVal('jira_timeline_harmonisation_m3', 'isEnabled', false)
			? ['software-roadmap-early-entry']
			: [],
	},
	afterPaint: [LazyIssueApp],
	entryPoint: () => {
		const [harmonisedEP, softwareEP] = fg('navx-global-side-nav')
			? [GSNSoftwareHarmonisedTimelinePageEntrypoint, GSNSoftwareTimelinePageEntrypoint]
			: [softwareHarmonisedTimelinePageEntrypoint, softwareTimelinePageEntrypoint];

		return expVal('jira_timeline_harmonisation_m3', 'isEnabled', false) ? harmonisedEP : softwareEP;
	},
	ufoName: expVal('jira_timeline_harmonisation_m3', 'isEnabled', false)
		? 'software.timeline-view'
		: 'next-gen-roadmap',
	supportsPreviewPanel: true,
	supportsProjectTheme: expVal('jira_timeline_harmonisation_m3', 'isEnabled', false)
		? 'full'
		: 'contoured',

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));

const SoftwareTimelineSkeleton = () => (
	<Box xcss={timelineSkeletonContainerStyles}>
		<RoadmapSkeleton />
	</Box>
);

const timelineSkeletonContainerStyles = xcss({
	height: '100%',
	backgroundColor: 'elevation.surface',
	borderRadius: 'radius.large',
	overflow: 'hidden',
});
